/**
 * Client-side policy definitions.
 * Maps policy names to permission requirements.
 * Must stay in sync with server/src/auth/policies.ts.
 */

interface PolicyDef {
  require?: string[];
  requireAny?: string[];
  systemOrg?: boolean;
}

export const POLICIES: Record<string, PolicyDef> = {
  // Platform admin
  RequireSuperadmin: { require: ['manage:users', 'manage:system'], systemOrg: true },
  CanManageUsers: { require: ['manage:users'] },
  CanManageSystem: { require: ['manage:system'] },
  CanManageRoles: { require: ['manage:roles'] },
  CanCreateOrgs: { require: ['manage:create-orgs'] },
  CanManageOrgs: { require: ['manage:organizations'] },

  // Trading
  CanTrade: { require: ['trade:execute'] },
  CanViewPortfolio: { require: ['trade:view-portfolio'] },
  CanManagePortfolio: { require: ['trade:manage-portfolio'] },
  CanManageRisk: { require: ['trade:manage-risk'] },
  CanManageStrategies: { require: ['trade:manage-strategies'] },

  // AI Agents
  CanViewAgents: { require: ['agents:view'] },
  CanManageAgents: { require: ['agents:manage'] },
  CanConverse: { require: ['agents:converse'] },
  CanManageWorkflows: { require: ['agents:manage-workflows'] },
  CanExecuteWorkflows: { require: ['agents:execute-workflows'] },
  // Market-data ingestion jobs — org power admin only (NOT analyst).
  CanRunIngestionJobs: { require: ['finance:ingestion-jobs:run'] },
  CanManageSkills: { require: ['agents:manage-skills'] },
  CanManageAgentTemplates: { require: ['agents:manage-templates'] },
  CanManageWorkforce: { require: ['workforce:manage'] },
  CanReviewDaemonDecision: { require: ['workforce:review-decision'] },
  CanApproveWorkflowGates: { require: ['workflows:approve-gate'] },
  CanCommitStrategyPromotion: { require: ['trade:commit-strategy-promotion'] },
  CanCreateSubagents: { require: ['agents:manage'] },
  CanDiscoverCapabilities: { requireAny: ['capabilities:discover', 'capabilities:recommend', 'agents:view'] },
  CanRecommendCapabilities: { requireAny: ['capabilities:recommend', 'agents:manage'] },
  CanEvaluateCapabilities: { requireAny: ['capabilities:evaluate', 'agents:manage'] },
  CanInstallMarketplace: { requireAny: ['marketplace:install', 'marketplace:adopt', 'agents:manage'] },

  // Research
  CanViewResearch: { requireAny: ['research:view-research', 'research:view-analysis'] },
  CanManageWatchlists: { require: ['research:manage-watchlists'] },
  CanViewMarketData: { require: ['research:view-market-data'] },

  // Dashboards
  CanViewDashboards: { require: ['dashboards:view'] },
  CanManageDashboards: { require: ['dashboards:manage'] },

  // Organization
  CanManageOrgMembers: { require: ['org:manage-members'] },
  CanInviteUsers: { require: ['org:invite-users'] },
  CanViewOrg: { require: ['org:view'] },
  CanManageIntegrations: { require: ['org:manage-settings'] },
  CanManageOrgRuntimeFlags: { require: ['org:manage-settings'] },

  // Settings
  CanManageNotifications: { require: ['settings:notifications'] },

  // Internal
  CanViewInternalDocs: { require: ['internal:view-docs'] },

  // Memory (Spec 51)
  CanManageOrgMemory: { require: ['memory:manage_org'] },

  // Memories & Artifacts (Spec 58)
  CanViewMemoriesArtifacts: { require: ['memories:view'] },
  CanCreateMemoriesArtifacts: { require: ['memories:create'] },
  CanEditMemoriesArtifacts: { require: ['memories:edit'] },
  CanDeleteMemoriesArtifacts: { require: ['memories:delete'] },

  // Teams (Spec 60)
  CanManageTeams: { require: ['org:manage-teams'] },

  // Projects (Spec 59)
  CanViewProjects: { require: ['projects:view'] },
  CanManageProjects: { require: ['projects:manage'] },
  CanInviteToProjects: { require: ['projects:invite'] },
  CanAccessProjectCode: { require: ['projects:code:access'] },
  CanManageProjectCode: { require: ['projects:code:manage'] },
  CanAdminProjectCode: { require: ['projects:code:admin'] },
  CanReviewProjectCode: { require: ['projects:review-code'] },
  CanViewJourneyEvidence: { require: ['platform:evidence:view'] },
  CanViewRawJourneyEvidence: { require: ['platform:evidence:view_raw'] },

  // Tasks (Spec 61)
  CanManageTasks: { require: ['tasks:manage'] },
  CanApproveTaskAction: { require: ['tasks:approve'] },

  // Workstreams (Spec 62)
  CanViewWorkstreams: { require: ['workstreams:view'] },
  CanManageWorkstreams: { require: ['workstreams:manage'] },
  CanManageWorkstreamMembers: { require: ['workstreams:manage-members'] },

  // Approver delegations (Spec 62 D10)
  CanManageApproverDelegations: { require: ['approvals:manage-delegations'] },

  // Baseline
  IsAuthenticated: { require: [] },

  // Worker SDK (Spec 78 WS2)
  CanManageWorkerSdkKeys: { require: ['worker:manage_keys'] },
  // Platform MCP endpoint (Track S) — held by every approved user; gates self-issuing a user key.
  CanUseAgentMcp: { require: ['agent:mcp'] },

  // API-key-only policies — JWT browser sessions never satisfy these.
  // The client-side evaluator always returns false for them (no matching
  // permission in a JWT user's set), which is the correct UX behaviour: only
  // machine-to-machine callers hold these scopes.
  CanInvokeWorker: { require: ['worker:invoke'] },
  CanInvokeWorkerWs: { require: ['worker:invoke_ws'] },
  CanRegisterAiWorker: { require: ['worker:register_ai'] },
  CanReportHostMetrics: { require: ['system:host_metrics:write'] },
  CanPushSchema: { require: ['deployment:push_schema'] },
  CanPushCode: { require: ['deployment:push_code'] },
  CanEnableModule: { require: ['deployment:enable_module'] },

  // Evaluation Framework (EVAL-FW)
  CanManageEvaluationTemplates: { require: ['evaluation:manage-templates'] },
  CanApplyEvaluation: { require: ['evaluation:apply-template'] },

  // Finance Audit Ledger (HFP #12/F4)
  CanViewAuditLedger: { require: ['finance:audit-ledger:read'] },
  CanVerifyAuditLedger: { require: ['finance:audit-ledger:verify'] },

  // Finance Execution Bridge (HFP F6)
  CanDeployStrategy: { require: ['finance:strategy:deploy'] },

  // Finance Model Lineage Registry (HFP #27) — read-only governance surface
  CanViewModelLineage: { require: ['finance:model-lineage:read'] },

  // Finance Strategy Results (S04) — read-only run results + artifacts surface
  CanViewStrategyResults: { require: ['finance:strategy-results:read'] },

  // Finance broker credentials (HFP #26) — per-org broker key management (org_admin only)
  CanManageBrokers: { require: ['finance:broker-credentials:manage'] },

  // Self-Improvement (S1) — improvement-proposal review surface
  CanViewImprovements: { require: ['improvements:view'] },
  CanDecideImprovements: { require: ['improvements:decide'] },
};

/**
 * Evaluate a policy against a permission set.
 */
export function evaluateClientPolicy(
  policyName: string,
  permissions: string[],
  organizationPlan?: string | null,
): boolean {
  const policy = POLICIES[policyName];
  if (!policy) return false;

  if (policy.systemOrg && organizationPlan !== 'system') {
    return false;
  }

  if (policy.require && policy.require.length > 0) {
    if (!policy.require.every((p) => permissions.includes(p))) return false;
  }
  if (policy.requireAny && policy.requireAny.length > 0) {
    if (!policy.requireAny.some((p) => permissions.includes(p))) return false;
  }

  return true;
}
